import { ClearViewUtils } from "../utils";

export class CvLocalizationServiceBase {
    private loadedSections: Map<string, Map<string, string>> = new Map();
    private sectionPromises: Map<string, Promise<Map<string, string>>> = new Map();

    constructor(
        fromSharedService: boolean = false,
        private $window?: Window,
    ) {
        if (fromSharedService)
            return CvLocalizationServiceBase.getServiceBase();

        if (!$window)
            this.$window = window;
    }

    static getServiceBase(): CvLocalizationServiceBase {
        return ClearViewUtils.getSharedService(CvLocalizationServiceBase, 'CvLocalizationServiceBase');
    }

    public GetSection(sectionName: string) {
        if (this.loadedSections.has(sectionName)) // section already loaded
            return Promise.resolve(this.loadedSections.get(sectionName));

        if (this.sectionPromises.has(sectionName)) // section already loading
            return this.sectionPromises.get(sectionName);

        // load section
        const promise = new Promise<Map<string, string>>((resolve, reject) => {
            if (!this.$window['LanguageWebService']) {
                resolve(null);
            }

            this.$window['LanguageWebService'].LoadSection(sectionName,
                result => {
                    const sectionMap = this.endpointDictionaryToMap(result);

                    if (sectionMap == null) {
                        reject(`Localization Error: "${sectionName}" is not a valid section name.`);
                    } else {
                        this.loadedSections.set(sectionName, sectionMap);
                        resolve(sectionMap);
                    }
                },
                /*error => {
                    reject(error);
                }*/
            );
        });

        this.sectionPromises.set(sectionName, promise);
        return promise;
    }

    public async GetLocalText(sectionName: string, keyName: string, defaultValue = keyName) {
        const sectionMap = await this.GetSection(sectionName);
        if (sectionMap && sectionMap.has(keyName))
            return sectionMap.get(keyName);
        else
            return defaultValue;
    }

    // Replace variable placeholders ({#}) with given arguments.
    public Format(format: string, ...args: (string | number)[]): string {
        let result = "";
        let interpolation = false;
        let index = "";

        for (let i = 0; i < format.length; i++) {
            if (i < format.length - 1 && format[i] === '{') {
                if (format[i + 1] === '{') {
                    result += '{';
                    i++;
                }
                else {
                    interpolation = true;
                }
            }
            else if (i < format.length && format[i] === '}') {
                if (i < format.length - 1 && format[i + 1] === '}') {
                    result += '}';
                    i++;
                }
                else {
                    interpolation = false;
                    const numI = Number(index);
                    index = "";

                    if (Number.isInteger(numI) && numI < args.length) {
                        result += args[numI];
                    }
                }
            }
            else if (interpolation) {
                index += format[i];
            }
            else {
                result += format[i];
            }
        }

        return result;
    }

    /**
     * Returns the localization value for the specified `section` and `key`, if cached.
     * This function will not load localizations or wait for them.
     *
     * @param section Section from the *.language file
     * @param key Key value within the `section`
     * @param defaultValue Default value to return if the section hasn't been loaded or key doesn't exist.
     */
    public getCachedText(section: string, key: string, defaultValue = key): string {
        const sectionMap: Map<string, string> = this.loadedSections.get(section);
        if (sectionMap?.has(key)) {
            return sectionMap.get(key);
        }
        return defaultValue;
    }

    private endpointDictionaryToMap(dictionary: { [key: string]: string[] }) {
        try {
            const flattenedDictionaryEntries = Object.entries(dictionary ?? {}).map((val) => {
                const entry: [string, string] = [val[0], val[1][0]];
                return entry;
            });

            return new Map<string, string>(flattenedDictionaryEntries);
        } catch {
            return null;
        }
    }
}
